<template>
    <div id="kingpin-selector">

        <div id="kingpin-loading" class="container" v-if="!isLoaded  && !isLoadFailed">
            <h2 class="loading-text">Loading<span>.</span><span>.</span><span>.</span></h2>
        </div>

        <div class="tabs" v-if="isLoaded">
            <div class="container">
                <button class="tab"
                        @click="switchMode('pin')"
                        :class="{active: this.inputType === 'pin'}">
                    <span>Kingpin by Measurements</span>
                </button>
                <button class="tab"
                        @click="switchMode('vehicle')"
                        :class="{active: this.inputType === 'vehicle'}">
                    <span>Kingpin by Vehicle</span>
                </button>
            </div>
        </div>

        <form id="pinInput" v-if="inputType === 'pin' && isLoaded" v-on:submit.prevent>
            <div class="container">
                <label id="pin-type-select-label" for="pin-type-select">Pin Type</label>
                <fieldset id="pin-type-select">
                    <div v-for="pin in pins" :key="pin.type" class="pin-type-wrapper">
                        <input type="radio"
                               :id="'pin-type-' + pin.type"
                               :name="'pin-type-' + pin.type"
                               :value="pin.type"
                               v-model="input.type">
                        <label :for="'pin-type-' + pin.type">
                            <img :src="'../wp-content/plugins/kingpin/public/img/pin-type-' + pin.type + '.png'" alt="">
                            <span class="pin-name">Type {{pin.type.toUpperCase()}}</span>
                            <span class="pin-description">{{pin.description}}</span>
                        </label>
                    </div>
                </fieldset>

                <fieldset class="range">
                    <label for="inputKingpinLength">Length</label>
                    <div class="range-input">
                        <input type="range"
                               step="0.01"
                               :min="this.sizeRange.length.min"
                               :max="this.sizeRange.length.max"
                               v-model="input.length">
                        <div class="num-input">
                            <input type="number"
                                   id="inputKingpinLength"
                                   step="0.01"
                                   :min="this.sizeRange.length.min"
                                   :max="this.sizeRange.length.max"
                                   v-model.lazy="input.length">
                            <div class="num-control">
                                <button class="num-btn"
                                        @click="adjustPinInput('length', 2)"
                                        :class="{disabled: isNumButtonDisabled(this.input.length, this.sizeRange.length.max, 'max')}"
                                        :disabled="isNumButtonDisabled(this.input.length, this.sizeRange.length.max, 'max')"
                                >
                                    +
                                </button>
                                <button class="num-btn"
                                        @click="adjustPinInput('length', 2, 'subtraction')"
                                        :class="{disabled: isNumButtonDisabled(this.input.length, this.sizeRange.length.min, 'min')}"
                                        :disabled="isNumButtonDisabled(this.input.length, this.sizeRange.length.min, 'min')"
                                >
                                    -
                                </button>
                            </div>
                        </div>
                    </div>
                </fieldset>

                <fieldset class="range">
                    <label for="inputKingpinDiameter">Diameter</label>
                    <div class="range-input">
                        <input type="range"
                               step="0.001"
                               :min="this.sizeRange.diameter.min"
                               :max="this.sizeRange.diameter.max"
                               v-model="input.diameter">
                        <div class="num-input">
                            <input type="number"
                                   step="0.001"
                                   :min="this.sizeRange.diameter.min"
                                   :max="this.sizeRange.diameter.max"
                                   v-model.lazy="input.diameter">
                            <div class="num-control">
                                <button class="num-btn"
                                        @click="adjustPinInput('diameter', 3)"
                                        :class="{disabled: isNumButtonDisabled(this.input.diameter, this.sizeRange.diameter.max, 'max')}"
                                        :disabled="isNumButtonDisabled(this.input.diameter, this.sizeRange.diameter.max, 'max')"
                                >
                                    +
                                </button>
                                <button class="num-btn"
                                        @click="adjustPinInput('diameter', 3, 'subtraction')"
                                        :class="{disabled: isNumButtonDisabled(this.input.diameter, this.sizeRange.diameter.min, 'min')}"
                                        :disabled="isNumButtonDisabled(this.input.diameter, this.sizeRange.diameter.min, 'min')"
                                >
                                    -
                                </button>
                            </div>
                        </div>

                    </div>
                </fieldset>
            </div>
        </form>

        <form id="vehicleInput" v-if="inputType === 'vehicle'  && isLoaded">
            <div class="container">
                <fieldset class="select-element">
                    <label for="makes-select">Vehicle Make</label>
                    <select class="form-control" id="makes-select" name="makes-select" v-model="vehicleInput.make">
                        <option value="" disabled selected>Pick a Make</option>
                        <option v-for="make in makes" v-bind:value="make.slug" :key="make.term_id">
                            {{ make.name }}
                        </option>
                    </select>
                </fieldset>


                <fieldset v-if="hasMultipleModels" class="select-element">
                    <!-- Model Select -->
                    <label for="models-select">Vehicle Model</label>
                    <select class="form-control" id="models-select" name="models-select" v-model="vehicleInput.model">
                        <option value="" disabled selected>Pick a Model</option>
                        <option v-for="model in modelList" v-bind:value="model.value" :key="model.value">
                            {{ model.name }}
                        </option>
                    </select>
                </fieldset>


                <fieldset v-if="String(vehicleInput.model).length > 0 && this.numberOfAxles > 1" class="select-element">
                    <!-- Axel Select -->
                    <label for="axle-select">Axle</label>
                    <select class="form-control" id="axle-select" name="axle-select" v-model="vehicleInput.axle">
                        <option value="" disabled selected>Pick an Axle</option>
                        <option v-for="(axle, index) in models[vehicleInput.model].axles" v-bind:value="index" :key="index">
                            {{ axle.axleName }}
                        </option>
                    </select>
                </fieldset>


                <fieldset v-if="String(vehicleInput.axle).length > 0 && models[vehicleInput.model].axles[vehicleInput.axle].pins.length > 1" class="select-element">
                <!-- Year Select -->
                <label for="years-select">Years</label>
                <select class="form-control" id="years-select" name="years-select" v-model="vehicleInput.years">
                    <option value="" disabled selected>Pick an year range</option>
                    <option v-for="(pin, index) in models[vehicleInput.model].axles[vehicleInput.axle].pins" v-bind:value="index" :key="index">
                        {{ pin.yearsFormatted }}
                    </option>
                </select>
            </fieldset>
            </div>
        </form>

        <div v-if="isFormValid && isLoaded" id="kingpin-results" class="kingpins">
            <div class="container">
                <div class="results-title">
                    <h2>Matching Kingpins</h2>
                </div>
                <div class="pins">
                    <div id="result-qwikkit" class="kingpin-result">
                        <h4 class="product-type">QwikKit<sup>®</sup> King Pin Kit</h4>
                        <div v-if="matchedKingpins.qwikkit.length > 0">
                            <KingpinItem v-for="kingpin in matchedKingpins.qwikkit" :kingpin="kingpin" :timesUpdated="timesUpdated" :inputType="inputType" :key="kingpin['ID'] "></KingpinItem>
                        </div>
                        <h4 v-else class="no-kingpins">None Available</h4>
                    </div>
                    <div id="result-pluskit" class="kingpin-result">
                        <h4 class="product-type">PlusKit<sup>®</sup> King Pin Kit</h4>
                        <div v-if="matchedKingpins.pluskit.length > 0">
                            <KingpinItem v-for="kingpin in matchedKingpins.pluskit" :kingpin="kingpin" :timesUpdated="timesUpdated" :inputType="inputType" :key="kingpin['ID'] "></KingpinItem>
                        </div>
                        <h4 v-else class="no-kingpins">None Available</h4>
                    </div>
                    <div id="result-econokit" class="kingpin-result">
                        <h4 class="product-type">ECONO KIT<sup>™</sup> Bushing Kit</h4>
                        <div v-if="matchedKingpins.econokit.length > 0">
                            <KingpinItem v-for="kingpin in matchedKingpins.econokit" :kingpin="kingpin" :timesUpdated="timesUpdated" :inputType="inputType" :key="kingpin['ID'] "></KingpinItem>
                        </div>
                        <h4 v-else class="no-kingpins">None Available</h4>
                    </div>
                </div>
            </div>
        </div>

        <div id="kingpin-load-failed" v-if="isLoadFailed">
            <div class="container">
                <h2>The Kingpin Selector Tool has failed to load</h2>
                <h4>Please try reloading the page or try again later</h4>
            </div>
        </div>

    </div>
</template>

<script>

import KingpinItem from "./components/KingpinItem";
import axios from "axios";

export default {
    name: 'App',
    data() {
        return {
            ajaxURL: this.getAjaxURL(),
            input: {
                diameter: '',
                length: '',
                type: '',
            },
            inputType: 'pin',
            isLoadFailed: false,
            isAjaxLoaded: {
                kingpins: false,
                makes: false,
            },
            kingpins: [],
            makes: [],
            models: [],
            matchedKingpins: {
                econokit: [],
                pluskit: [],
                qwikkit: [],
            },
            pins: [
                {
                    type: 'a',
                    description: 'Single Flat Lock'
                },
                {
                    type: 'b',
                    description: 'Double Flat Lock'
                },
                {
                    type: 'c',
                    description: 'Same Side Lock Flats'
                },
                {
                    type: 'e',
                    description: 'Tapered Threaded'
                },
                {
                    type: 'f',
                    description: 'Tapered'
                },
                {
                    type: 'g',
                    description: 'Step Threaded'
                },
                {
                    type: 'h',
                    description: 'No Lock Flats'
                },
                {
                    type: 'j',
                    description: 'Single Step'
                },
            ],
            sizeRange: {
                diameter: {
                    min: '',
                    max: ''
                },
                length: {
                    min: '',
                    max: ''
                }
            },
            timesUpdated: 0,
            tolerance: {
                diameter: {
                    upper: 0.004, // doubled from 0.002
                    lower: 0.010, // doubled from 0.005
                },
                length: {
                    upper: 0.20, // doubled from 0.10
                    lower: 0.30, // doubled from 0.15
                }
            },
            vehicleInput: {
                make: '',
                model: '',
                axle: '',
                years: ''
            },
        }
    },
    components: {
        KingpinItem
    },
    created() {
        this.getKingpinsAjax();
        this.getMakesAjax();
    },
    methods:{
        /**
         * Get AJAX URL
         * Gets the WordPress AJAX.php file location
         * @return {String} The URL
         */
        getAjaxURL(){
            let currentURL = window.location;
            let baseURL = currentURL.protocol + "//" + currentURL.host + "/";
            return baseURL + "wp-admin/admin-ajax.php";
        },

        /**
         * Get Kingpins via AJAX
         * Get the kingpins from WordPress using AJAX and add them to the kingpins[] array
         * @requires axios
         */
        async getKingpinsAjax(){
            let params = new URLSearchParams();
            params.append('action', 'get_kingpins');
            axios.post(this.ajaxURL, params)
                .then( response => {
                    this.kingpins = response.data;

                    // Cast the length and diameter as numbers (they come in as strings)
                    this.kingpins.forEach( kingpin => {
                        kingpin.length = Number(kingpin.length);
                        kingpin.diameter = Number(kingpin.diameter);
                        kingpin.deviation = {};
                    })

                    // Get the min max of the ranges
                    this.sizeRange.diameter.max = this.getMinMax(this.kingpins, 'diameter', 'max');
                    this.sizeRange.diameter.min = this.getMinMax(this.kingpins, 'diameter', 'min');
                    this.sizeRange.length.max = this.getMinMax(this.kingpins, 'length', 'max');
                    this.sizeRange.length.min = this.getMinMax(this.kingpins, 'length', 'min');

                    // Set Load State to true
                    this.isAjaxLoaded.kingpins = true;
                })
                .catch(error => {
                    console.log(error);
                    this.isLoadFailed = true;
                });
        },

        /**
         * Get Makes via AJAX
         * Get the terms from the Makes taxonomy in WordPress using AJAX and add them to the makes[] array
         * @requires axios
         */
        async getMakesAjax(){
            let params = new URLSearchParams();
            params.append('action', 'get_vehicle_makes');

            axios.post(this.ajaxURL, params)
                .then( response => {
                    this.makes = response.data;
                    this.isAjaxLoaded.makes = true;
                })
                .catch(error => {
                    console.log(error);
                    this.isLoadFailed = true;
                });
        },

        /**
         * Get Min or Max
         * Loops through an object and compares the values of a specific key and returns the smallest or largest number
         * @param {Object} object The object to loop through
         * @param {String} key the key for the numbers to be compared
         * @param {string} direction Optional. Whether to get the largest or smaller number. "max" for largest, "min" for smallest
         *
         * @return {Number}
         */
        getMinMax(object, key, direction = 'max'){
            let nums = [];
            Object.values(object).forEach( val => {
                nums.push(val[key])
            });

            if (direction === 'max') {
                return Math.max(...nums);
            } else if (direction === 'min') {
                return Math.min(...nums)
            }
        },

        /**
         * Get Number Difference
         * Get the difference between two numbers
         * @return {Number} the difference between two numbers
         */
        getDifference(a, b, decimalPlaces = 2){
            return Number(Math.abs(a - b).toFixed(decimalPlaces));
        },

        /**
         * Get Tolerances
         * Get the differences between the form input and a kingpin
         * @param {Object} kingpin The kingpin object
         */
        getTolerances(kingpin){
            kingpin.deviation.length = {}
            kingpin.deviation.diameter = {}

            // Check if the deviation is up or down
            kingpin.deviation.length.direction = (kingpin.length - this.input.length) <= 0 ? 'upper' : 'lower';
            kingpin.deviation.diameter.direction = (kingpin.diameter - this.input.diameter) <= 0 ? 'upper' : 'lower';

            // Get the deviation amount
            kingpin.deviation.length.amount = this.getDifference(kingpin.length, this.input.length,);
            kingpin.deviation.diameter.amount = this.getDifference(kingpin.diameter, this.input.diameter, 3);

            // Get the total amount of deviation
            kingpin.deviation.total = Number((kingpin.deviation.length.amount + kingpin.deviation.diameter.amount).toFixed(3));

            // Check to see if the kingpin is within tolerance of the input
            if ( kingpin.deviation.length.direction === 'upper' && kingpin.deviation.length.amount > this.tolerance.length.upper ) {
                kingpin.isWithinTolerance = false;
            }
            else if (kingpin.deviation.length.direction === 'lower' && kingpin.deviation.length.amount > this.tolerance.length.lower ) {
                kingpin.isWithinTolerance = false;
            }
            else if ( kingpin.deviation.diameter.direction === 'upper' && kingpin.deviation.diameter.amount > this.tolerance.diameter.upper ) {
                kingpin.isWithinTolerance = false;
            }
            else if (kingpin.deviation.diameter.direction === 'lower' && kingpin.deviation.diameter.amount > this.tolerance.diameter.lower ) {
                kingpin.isWithinTolerance = false;
            }
            else {
                kingpin.isWithinTolerance = true;
            }
        },

        /**
         * Find the Closest Kingpin
         * finds the kingpin with the smallest tolerance and pushes it to the matchedKingpins array for the given product
         * @param {Array} matchedKingpins An array for kingpins to match the given kingpin to
         * @param {Object} kingpin A kingpin object to match against the array of kingpins
         */
        findClosestKingpin( matchedKingpins, kingpin){
            // Push the kingpin to the array if the array is empty
            if (matchedKingpins.length === 0){
                matchedKingpins.push(kingpin);
            } else {
                // Loop through the matchedKingpins array
                matchedKingpins.forEach( (matchedKingpin) => {
                    // If the kingpin has a smaller total deviation than what it is being compared to, replace it in the array
                    if (kingpin.deviation.total < matchedKingpin.deviation.total){
                        // console.log( kingpin.title + ' ' + kingpin.deviation.total + ' < ' + matchedKingpin.title + ' ' + matchedKingpin.deviation.total )
                        matchedKingpins.splice(0, 1, kingpin)
                    }
                })
            }
        },

        /**
         * Compare Kingpin
         * Get the tolerances of a kingpin and attempts to add it to the matchedKingpins[] array
         * @param {Object} kingpin The kingpin object
         */
        compareKingpin(kingpin){

            // Get the tolerances
            this.getTolerances(kingpin);

            // If the kingpin is within tolerance, check it against the other kingpins
            // if (kingpin.isWithinTolerance) {
                if (kingpin.product === 'econokit') {
                    this.findClosestKingpin(this.matchedKingpins.econokit, kingpin);
                }
                else if (kingpin.product === 'qwikkit'){
                    this.findClosestKingpin(this.matchedKingpins.qwikkit, kingpin);
                }
                else if ( kingpin.product === 'pluskit') {
                    this.findClosestKingpin(this.matchedKingpins.pluskit, kingpin);
                }
            // }
        },

        /**
         * Swap Mode
         * @param {String} mode The kingpin object
         */
        switchMode(mode){
            this.inputType = mode;
        },

        /**
         * Adjust Pin Input
         * Add or Subtract from the given input
         * @param {String} inputType What input to adjust
         * @param {Number} decimals How many numbers after the decimal point. Must be an Integer
         * @param {String} direction Addition or Subtraction
         */
        adjustPinInput(inputType, decimals, direction = 'addition'){
            // The current number
            let val = Number(parseFloat(this.input[inputType]).toPrecision(12));

            // How much to add or subtract
            let increment = Math.pow(10, -decimals)

            if (direction === 'addition'){
                if (!this.input[inputType]){
                    this.input[inputType] = this.sizeRange[inputType].min;
                } else if (val >=this.sizeRange[inputType].max) {
                    this.input[inputType] = this.sizeRange[inputType].max;
                } else {
                    this.input[inputType] = Number(val + increment).toFixed(decimals);
                }
            }

            else if (direction === 'subtraction'){
                if (!this.input[inputType]){
                    this.input[inputType] = this.sizeRange[inputType].max;
                } else if (val <=this.sizeRange[inputType].min) {
                    this.input[inputType] = this.sizeRange[inputType].min;
                } else {
                    this.input[inputType] = Number(val - increment).toFixed(decimals);
                }
            }

        },

        /**
         * Is Number Button Disabled
         * determines if the number button should be disabled or not
         * @param {Number|String} input the input number
         * @param {Number} limit The max or min point
         * @param {String} direction max or min
         */
        isNumButtonDisabled(input, limit, direction = 'max'){
            if (input.length === 0) {
                return false;
            } else {
                if (direction === 'max'){
                    return input >= limit;
                } else if (direction === 'min'){
                    return input <= limit;
                } else {
                    return false;
                }
            }
        }
    },
    computed: {
        /**
         * Computed: Check if the Input form is valid
         * Checks to see if all inputs have been filled
         *
         * @return boolean
         */
        isFormValid(){
            return !!(this.input.type && this.input.length && this.input.diameter);
        },

        /**
         * Computed: Matched Kingpins Status
         * Checks to see if there are any matched kingpins
         * @return boolean
         */
        isMatchedKingpinsEmpty() {
            return this.matchedKingpins.qwikkit.length === 0 && this.matchedKingpins.econokit.length === 0 && this.matchedKingpins.pluskit.length === 0;
        },

        /**
         * Computed: Input Diameter
         * Allows for watching of this.input.diameter changes
         * @return Number
         */
        inputDiameter() {
            return Number(this.input.diameter);
        },

        /**
         * Computed: Input Length
         * Allows for watching of this.input.length changes
         * @return Number
         */
        inputLength() {
            return Number(this.input.length);
        },

        /**
         * Computed: Model List
         * Build a list of all possible models for a given make
         * @return Array
         */
        modelList(){
            let models = [];

            this.models.forEach((model, index) => {
                if (model.hasMultipleModels){
                    model.models.forEach( (m) => {
                        // models.push(m.model_name);
                        models.push({
                            name: m.model_name,
                            value: index,
                        });
                    })
                } else {
                    models.push({
                        name: model.title,
                        value: index,
                    });
                }
            });

            // Sort models by alpha
            models.sort((a, b) => {
                if(a.name < b.name) { return -1; }
                if(a.name > b.name) { return 1; }
                return 0;
            })

            return models;
        },

        /**
         * Computed: Multiple Models
         * Check if a make has more than one model associated with it.
         * @return Boolean
         */
        hasMultipleModels(){
            return this.modelList.length > 1;
        },

        /**
         * Computed: Check if the Input form is valid
         * Checks to see if all vehicle inputs have been filled
         *
         * @return boolean
         */
        isVehicleInputValid(){
            if (typeof this.vehicleInput.model === "number" ){
                if (typeof this.vehicleInput.axle === "number"){
                    if(this.models[this.vehicleInput.model].axles[this.vehicleInput.axle].pins.length > 1){
                        return typeof this.vehicleInput.years === "number";
                    } else {
                        return true;
                    }
                } else  {
                    return false;
                }
            } else {
                return false;
            }
        },

        /**
         * Computed: Check if Ajax is loaded
         *
         * @return boolean
         */
        isLoaded(){
            return Object.values(this.isAjaxLoaded).every( value => value === true);
        },

        /**
         * Computed: Number of Axles
         * Checks the number of axles associated with the input vehicle model
         *
         * @return Number
         */
        numberOfAxles(){
            if (this.vehicleInput.model !== "") {

                // return this.models[this.vehicleInput.model].axles[this.vehicleInput.axle].pins.length
                return this.models[this.vehicleInput.model].axles.length;
            } else {
                return 0;
            }

        }
    },
    watch: {
        // Watch: Form Inputs
        input: {
            handler(){
                if (this.isFormValid){

                    // clear matched kingpins
                    this.matchedKingpins = {
                        econokit: [],
                        pluskit: [],
                        qwikkit: [],
                    };

                    // Run checker
                    Object.values(this.kingpins).forEach( kingpin => {
                        if (this.input.type === kingpin['pin-type']){
                            this.compareKingpin(kingpin);
                        }
                    });

                    // Need to update this number to force kingpin component re-rendering
                    this.timesUpdated ++;
                }
            },
            deep: true
        },

        // Watch: Input Diameter
        inputDiameter() {
            // Check if input is out of range low
            if (this.input.diameter < this.sizeRange.diameter.min) {
                this.input.diameter = this.sizeRange.diameter.min;
            }

            // Check if input is out of range high
            else if (this.input.diameter > this.sizeRange.diameter.max) {
                this.input.diameter = this.sizeRange.diameter.max;
            }

            // TODO: Let user know is is out of range
        },

        // Watch: Input Length
        inputLength() {
            // Check if input is out of range low
            if (this.input.length < this.sizeRange.length.min) {
                this.input.length = this.sizeRange.length.min;
            }

            // Check if input is out of range high
            else if (this.input.length > this.sizeRange.length.max) {
                this.input.length = this.sizeRange.length.max;
            }

            // TODO: Let user know is is out of range
        },

        // Watch: Vehicle Make changes
        'vehicleInput.make': function (make){
            // Clear the vehicle input
            this.vehicleInput.model = '';
            this.vehicleInput.axle = '';
            this.vehicleInput.years = '';
            this.models = [];

            // Get the Models for a specific make via AJAX
            let params = new URLSearchParams();
            params.append('action', 'get_vehicle_models');
            params.append('make', make);

            axios.post(this.ajaxURL, params)
                .then( response => {

                    // Push the response data to the model list
                    response.data.forEach((data) => {
                        this.models.push(data);
                    });

                    this.models.forEach((model) => {
                        // Cast the data and format the returned data
                        model.axles.forEach((axle) => {
                            axle.axleName = axle['axle-name'];
                            axle.addYears = axle.addYears === "yes";
                            axle.diameter = Number(axle.diameter);
                            axle.length = Number(axle.length);

                            // Make Pin Object
                            axle.pins = [];
                            let pin = {
                                diameter: Number(axle.diameter),
                                length: Number(axle.length),
                                pinType: axle['pin-type'],
                            }
                            if (axle.addYears){
                                pin.yearStart = axle['axle-year-start'];
                                pin.yearEnd = axle['axle-year-end'];

                                if (pin.yearStart != false && pin.yearEnd != false){
                                    pin.yearsFormatted = pin.yearStart + ' - ' + pin.yearEnd;
                                } else if (pin.yearStart != false && pin.yearEnd == false){
                                    pin.yearsFormatted = pin.yearStart + ' - ' + 'present';
                                } else if (pin.yearStart == false && pin.yearEnd != 0){
                                    pin.yearsFormatted = "until " + pin.yearEnd;
                                }
                            }
                            axle.pins.push(pin);

                            // Remove Properties
                            delete axle['axle-name'];
                            delete axle['pin-type'];
                            delete axle['diameter'];
                            delete axle['length'];
                            delete axle['axle-year-start'];
                            delete axle['axle-year-end'];

                        })

                        // For Axles with multiple years, merge them
                        model.axles.forEach((axle, index) => {
                            for (let i = model.axles.length - 1; i >+ 0; i--){
                                if (index !== i && axle.axleName === model.axles[i].axleName ){
                                    axle.pins.push(...model.axles[i].pins);
                                    model.axles.splice(i, 1);
                                }
                            }
                        })

                    });

                    // Auto set the selected model is there is only one model
                    if (this.models.length === 1){
                        this.vehicleInput.model = 0;
                    }

                })
                .catch(error => {
                    console.log(error);
                });
        },

        // Watch: Vehicle Model changes
        'vehicleInput.model': function (){
            // Clear the vehicle input
            this.vehicleInput.axle = '';
            this.vehicleInput.years = '';

            if (typeof this.vehicleInput.model === "number" ){
                if (this.models[this.vehicleInput.model].axles.length <= 1 ){
                    this.vehicleInput.axle = 0;
                }
            }
        },

        // Watch: Vehicle Axle changes
        'vehicleInput.axle': function (){
            // Clear the vehicle input
            this.vehicleInput.years = '';
        },

        // Watch: Mode Switch
        inputType(){
            // Clean Input
            this.input.diameter = "";
            this.input.length = "";
            this.input.type = "";

            // Clean Vehicle Input
            this.vehicleInput.make = "";
            this.vehicleInput.model = "";
            this.vehicleInput.axle = "";
            this.vehicleInput.years = "";

            // Reset Models
            this.models = [];
        },

        // Watch: Vehicle Input change
        // Check for if valid, and if so fill the data from the selected pin to input variable
        vehicleInput: {
            handler(){
                if (this.isVehicleInputValid){
                    let pin = {};
                    if (this.models[this.vehicleInput.model].axles[this.vehicleInput.axle].pins.length > 1){
                        pin = this.models[this.vehicleInput.model].axles[this.vehicleInput.axle].pins[this.vehicleInput.years];
                    } else {
                        pin = this.models[this.vehicleInput.model].axles[this.vehicleInput.axle].pins[0];
                    }

                    this.input.diameter = pin.diameter;
                    this.input.length = pin.length;
                    this.input.type = pin.pinType;
                } else {
                    this.input.diameter = ""
                    this.input.length = "";
                    this.input.type = "";
                }
            },
            deep: true
        },

    },

}
</script>
